import { t } from '@hefe/i18n';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Typography } from 'antd';
import { useMemo, useState } from 'react';
import CustomTable from '../../components/table/custom-table.component';
import { useCrud } from '../hooks/use-crud';
import { CrudPathParams, CrudResource } from '../models/crud.types';
import CrudModal from './crud-modal.component';

interface CrudPageProps<T extends object> {
  resource: CrudResource<T>;
  params?: CrudPathParams;
  title?: string;
  description?: string;
}

export default function CrudPage<T extends object>(props: CrudPageProps<T>) {
  const { resource, params } = props;

  const crud = useCrud(resource, params);
  const [modalMode, setModalMode] = useState<'create' | 'update' | null>(null);
  const [selectedItem, setSelectedItem] = useState<T | undefined>(undefined);
  const [isSubmittingModal, setSubmittingModal] = useState(false);

  const tableName = useMemo(() => {
    if (resource.table.name) {
      return resource.table.name;
    }

    const endpoint = resource.endpoint.replace(/\//g, '-').replace(/:/g, '');
    const paramSuffix = params
      ? Object.values(params)
          .map((value) => String(value))
          .join('-')
      : 'root';

    return `crud-${endpoint}-${paramSuffix}`;
  }, [resource, JSON.stringify(params || {})]);

  const openCreateModal = () => {
    setSelectedItem(undefined);
    setModalMode('create');
  };

  const openUpdateModal = (item: T) => {
    setSelectedItem(item);
    setModalMode('update');
  };

  const closeLocalModal = () => {
    setModalMode(null);
    setSelectedItem(undefined);
    setSubmittingModal(false);
  };

  const activeFormId =
    modalMode === 'create'
      ? `crud-create-${tableName}`
      : `crud-update-${tableName}-${String(
          (selectedItem as any)?.[resource.rowKey] ?? 'unknown'
        )}`;

  const activeModalTitle =
    modalMode === 'create'
      ? resource.form.createTitle || t('add')
      : resource.form.editTitle || t('edit');

  return (
    <div>
      {props.title ? (
        <Typography.Title level={4}>{props.title}</Typography.Title>
      ) : null}
      {props.description ? (
        <Typography.Paragraph>{props.description}</Typography.Paragraph>
      ) : null}
      <CustomTable
        name={tableName}
        rowKey={resource.rowKey as string}
        dataSource={crud.data}
        columns={resource.table.columns as any}
        loading={crud.loading}
        onRowClick={(item) => openUpdateModal(item as T)}
        toolbarItems={[
          <Button
            key="crud-add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openCreateModal()}
          />,
        ]}
        deleteAction={{
          action: (ids: Array<string | number>, resetKeys?: () => void) => {
            Promise.all(ids.map((id) => crud.remove(id)))
              .then(() => {
                resetKeys?.();
                crud.reload();
              })
              .catch(() => null);
          },
          multiDelete: true,
        }}
      />
      <Modal
        title={activeModalTitle}
        open={modalMode !== null}
        destroyOnHidden
        onCancel={() => closeLocalModal()}
        onOk={() => {
          const form = document.getElementById(activeFormId) as HTMLFormElement;
          form?.requestSubmit();
        }}
        confirmLoading={isSubmittingModal}
        okButtonProps={{ loading: isSubmittingModal }}
      >
        {modalMode ? (
          <CrudModal
            key={activeFormId}
            formId={activeFormId}
            resource={resource}
            params={params}
            mode={modalMode}
            value={selectedItem}
            onSubmittingChange={setSubmittingModal}
            onSuccess={() => {
              closeLocalModal();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
}
